/**
 * The chat panel's pure half — `docs/features/chat.md`.
 *
 * What a transcript *is*, built two ways that must agree: live, from the
 * socket's frames as a turn streams, and on a resume, from the stored messages
 * (the Messages API's own blocks, as quevee saved them). Both end as the same
 * `ChatEntry[]`, so a resumed conversation reads exactly as it did when it was
 * live — the same tool lines, the same cost line under each answer.
 *
 * Pure, for `layout-templates.ts`' reason: the panel is where this app cannot
 * cheaply test, so nothing decidable lives there.
 */

import type { ChartGroup, Dock, Layout, Plot } from "@qufolio/model";

import type { StoredMessage } from "../api/chat";
import { chartSections } from "./active-section";
import { BRAND } from "./brand-current";
import { addPanel, isShowing } from "./dock";
import { available } from "./features";
import { chartGroupConfig } from "./section-config";

/** One client tool the agent ran, as a line under its answer. */
export interface ToolLine {
  /** `tool_use` id when stored; the bridge's call id when live. */
  id: string;
  name: string;
  args: Record<string, unknown>;
  /** Unsettled while the client is still running it. */
  state: "running" | "ok" | "refused";
  /** The refusal's sentence — a gesture-only action says why here. */
  reason?: string;
}

/** What one answer cost — the whole turn, every model call summed. */
export interface TurnCost {
  /** Billed (list × markup). Absent or null for an unpriced model. */
  billedUsd?: number | null;
  inputTokens: number;
  outputTokens: number;
}

export type ChatEntry =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string; tools: ToolLine[]; cost?: TurnCost }
  | { kind: "notice"; text: string };

/**
 * What quevee appends to the typed text (`agent.run_turn`), cut back off.
 *
 * The stored user message is the one the model saw — text, then the workspace
 * snapshot — because a resumed conversation replays it verbatim. The person
 * typed only the first half.
 */
export function typedText(content: string): string {
  const cut = content.indexOf("\n\n<workspace-context>");
  return cut === -1 ? content : content.slice(0, cut);
}

function blockText(block: Record<string, unknown>): string | undefined {
  return block.type === "text" && typeof block.text === "string" ? block.text : undefined;
}

function resultReason(content: unknown): string | undefined {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    const text = content.map((b) => (b && typeof b === "object" ? blockText(b as Record<string, unknown>) : undefined));
    const joined = text.filter(Boolean).join(" ");
    return joined || undefined;
  }
  return undefined;
}

/**
 * Stored messages to the transcript.
 *
 * A turn is a typed user message, then assistant messages and the tool
 * results between them; it reads as one answer — every text block joined, one
 * line per `tool_use` settled by its `tool_result`, and the turn's cost from
 * the last answer that carries one. Thinking blocks are the model's and are not
 * shown.
 *
 * `shown` picks which tools get a line: the client's own, as live, where a
 * server tool (`queryQvData`) never reaches the browser and so draws none.
 */
export function transcriptFromStored(
  messages: readonly StoredMessage[],
  shown: (name: string) => boolean = () => true,
): ChatEntry[] {
  const out: ChatEntry[] = [];
  let answer: Extract<ChatEntry, { kind: "assistant" }> | null = null;
  const lines = new Map<string, ToolLine>();

  const current = () => {
    if (!answer) {
      answer = { kind: "assistant", text: "", tools: [] };
      out.push(answer);
    }
    return answer;
  };

  for (const message of messages) {
    if (message.role === "user") {
      if (typeof message.content === "string") {
        out.push({ kind: "user", text: typedText(message.content) });
        answer = null;
        lines.clear();
        continue;
      }
      for (const block of message.content) {
        if (block.type !== "tool_result") continue;
        const line = lines.get(String(block.tool_use_id));
        if (!line) continue;
        if (block.is_error) {
          line.state = "refused";
          line.reason = resultReason(block.content);
        } else {
          line.state = "ok";
        }
      }
      continue;
    }
    const entry = current();
    const blocks = typeof message.content === "string" ? [{ type: "text", text: message.content }] : message.content;
    for (const block of blocks) {
      const text = blockText(block);
      if (text !== undefined) {
        entry.text = entry.text ? `${entry.text}\n\n${text}` : text;
      } else if (block.type === "tool_use" && shown(String(block.name))) {
        const line: ToolLine = {
          id: String(block.id),
          name: String(block.name),
          args: (block.input as Record<string, unknown>) ?? {},
          state: "running",
        };
        lines.set(line.id, line);
        entry.tools.push(line);
      }
    }
    if (typeof message.inputTokens === "number") {
      entry.cost = {
        billedUsd: message.billedUsd ?? null,
        inputTokens: message.inputTokens,
        outputTokens: message.outputTokens ?? 0,
      };
    }
  }
  // quevee stores only completed turns, and a completed turn answers every
  // `tool_use`; one left unsettled here is a row from before that rule, and a
  // spinner that can never stop is the worse thing to draw for it.
  for (const entry of out) {
    if (entry.kind !== "assistant") continue;
    for (const line of entry.tools) if (line.state === "running") line.state = "ok";
  }
  return out;
}

/**
 * The tool line's text: the action's own title, then what it was given.
 *
 * `title` comes from the action registry — the name a menu gives it, so the
 * line reads as something the user could have done themselves. Arguments are
 * the scalar ones, briefly: `SMA (21)` needs the catalog to spell, and an id is
 * noise, so a ULID-shaped value is left out.
 */
export function toolLabel(name: string, args: Record<string, unknown>, title?: string): string {
  const head = (title ?? name).replace(/…$/, "");
  const parts: string[] = [];
  for (const value of Object.values(args)) {
    if (typeof value === "string" && value && !/^[0-9A-HJKMNP-TV-Z]{26}$/.test(value)) parts.push(value);
    else if (typeof value === "number" || typeof value === "boolean") parts.push(String(value));
    // Settings ride as a list (`params: [8, 21, 13]`), and dropping them made
    // "Add indicator — macd" read the same whether the 8/21/13 arrived or not.
    else if (Array.isArray(value) && value.length && value.every((v) => typeof v === "number")) {
      const settings = `(${value.join(", ")})`;
      if (parts.length) parts[parts.length - 1] += ` ${settings}`;
      else parts.push(settings);
    }
  }
  const tail = parts.join(", ");
  if (!tail) return head;
  return `${head} — ${tail.length > 48 ? `${tail.slice(0, 47)}…` : tail}`;
}

const COUNT = new Intl.NumberFormat("en-US");

/**
 * `$0.0021 · 3,410 / 220 tokens` — billed, then tokens in and out.
 *
 * **Never the model.** The vendor is the admin's choice and the price is what
 * the reader pays; which model answered is not something they can act on. An
 * unpriced model shows tokens alone, never `$0`.
 */
export function formatCost(cost: TurnCost): string {
  const tokens = `${COUNT.format(cost.inputTokens)} / ${COUNT.format(cost.outputTokens)} tokens`;
  if (cost.billedUsd === null || cost.billedUsd === undefined) return tokens;
  const dollars = cost.billedUsd < 1 ? cost.billedUsd.toFixed(4) : cost.billedUsd.toFixed(2);
  return `$${dollars} · ${tokens}`;
}

const WHEN_PT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** A History row's date, in Pacific time — the owner's clock everywhere. */
export function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : `${WHEN_PT.format(at)} PT`;
}

/** The style fields `stylePlot` can change, as the plot holds them; undefined when none is set. */
function styleOf(plot: Plot): Record<string, unknown> | undefined {
  const st = plot.style;
  if (!st) return undefined;
  const out: Record<string, unknown> = {};
  for (const key of ["stroke", "thickness", "dashStyle", "markerShape", "markerSize"] as const) {
    if (st[key] !== undefined) out[key] = st[key];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * What each turn tells the agent about the screen — `UserMessage.context`.
 *
 * Which chart is active and what every chart on the layout holds, by id, so
 * "add a 21 sma" lands on the chart the person is looking at and a later
 * "remove it" can name the plot. Ids and spellings only: the agent targets
 * nodes, it does not render them.
 */
export function workspaceContext(layout: Layout | undefined, activeChartGroupId: string | undefined): Record<string, unknown> {
  const charts = chartSections(layout?.sections)
    .map((section) => chartGroupConfig(section))
    .filter((group): group is ChartGroup => group !== undefined)
    .map((group) => ({
      id: group.id,
      symbol: group.symbol,
      frequency: group.frequency,
      panes: (group.panes ?? []).map((pane) => ({
        id: pane.id,
        role: pane.role,
        plots: (pane.plots ?? []).map((plot) => ({
          id: plot.id,
          type: plot.type,
          ...(plot.outputAlias ? { alias: plot.outputAlias } : {}),
          // What `stylePlot` changes, as it stands — so "bigger" has a
          // starting point. Absent means the renderer's default.
          ...(styleOf(plot) ? { style: styleOf(plot) } : {}),
          ...(plot.hidden ? { hidden: true } : {}),
        })),
      })),
    }));
  return {
    ...(activeChartGroupId ? { activeChartGroupId } : {}),
    ...(layout ? { layout: layout.name } : {}),
    charts,
  };
}

/**
 * Whether the chat exists in this shell, and whether it has a key of its own.
 *
 * **Both gates, then the brand's placement** (`lib/features.ts`): the brand's
 * `agent` feature and the plan's `feat:agent` decide whether there is a panel
 * at all — `configured` adds that this build has a quevee to talk to. The slot
 * decides only the key: on the desktop a `hidden` agent still has a panel (Ask
 * AI opens it) and no strip key; on a phone the panel is reached only from More,
 * so anything but `more` is no chat there.
 *
 * `allowed` is `available("agent")` unless the caller already holds the hook's
 * answer (`useAvailable`), which re-renders when the plan arrives.
 */
export function chatGate(
  shell: "desktop" | "mobile",
  configured: boolean,
  allowed: boolean = available("agent"),
): { panel: boolean; key: boolean } {
  const slot = BRAND.slotOf("agent", shell);
  if (!allowed || !configured) return { panel: false, key: false };
  if (shell === "mobile") {
    const shown = slot === "more";
    return { panel: shown, key: shown };
  }
  return { panel: true, key: slot === "chartToolbar" };
}

/**
 * Ask AI's opener (`lib/chat-open.ts`): show the AI panel, then hand it the text.
 *
 * Shown the way a call opens the data panel — **beside** what the dock holds
 * (`addPanel`), never over it, so asking a question does not take the screener
 * away — and floated where the layout has no dock. Already showing, the dock is
 * left as it is: re-writing it would be a save for nothing.
 */
export function askAiOpener(deps: {
  hasDock: boolean;
  dock: Dock;
  setDock: (dock: Dock) => void;
  float: () => void;
  ask: (text: string) => void;
}): (text: string) => void {
  return (text) => {
    if (deps.hasDock) {
      if (!isShowing(deps.dock, "ai")) deps.setDock(addPanel(deps.dock, "ai"));
    } else {
      deps.float();
    }
    deps.ask(text);
  };
}

/** `Today: 3 of 50` — the agent budget after the last turn, as quevee counted it. */
export function budgetLine(budget: { used: number; limit: number } | undefined): string | null {
  return budget ? `Today: ${budget.used} of ${budget.limit}` : null;
}
